import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { createAssembly, createAgendaItem } from "endpoints/voting/index.action";
import { getAllCondos } from "endpoints/condo/index.action";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "./CreatePollModal.scss";

interface CreatePollModalProps {
  isOpen: boolean;
  toggle: () => void;
  onPollCreated?: () => void;
}

interface PollOption {
  id: string;
  text: string;
}

const CreatePollModal: React.FC<CreatePollModalProps> = ({ isOpen, toggle, onPollCreated }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    condo: "",
    title: "",
    description: "",
    startingDate: "",
    startingTime: "",
    endingDate: "",
    endingTime: "",
  });
  const [options, setOptions] = useState<PollOption[]>([
    { id: "1", text: "" },
    { id: "2", text: "" },
  ]);
  const [condominiums, setCondominiums] = useState<Array<{ id: string; name: string; condoNumber?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch condos when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchCondos = async () => {
        try {
          setLoading(true);
          setError(null);
          const response = await getAllCondos({ limit: 100, page: 1 });
          
          if (response?.status === 'success' && response?.data) {
            const condosList = response.data.condos || response.data || [];
            
            if (condosList.length === 0) {
              setError(t("polls.noCondominiumsFound"));
            } else {
              setCondominiums(condosList);
            }
          } else {
            setError(response?.message || t("polls.failedToLoadCondominiums"));
          }
        } catch (err: any) {
          console.error("Error fetching condos:", err);
          const errorMessage = err?.message || t("polls.failedToLoadCondominiums");
          setError(errorMessage);
          
          // If it's a 404 or network error, provide helpful message
          if (errorMessage.includes('404') || errorMessage.includes('Failed to fetch')) {
            setError(t("polls.cannotConnectToServer"));
          }
        } finally {
          setLoading(false);
        }
      };
      fetchCondos();
    } else {
      // Reset error when modal closes
      setError(null);
    }
  }, [isOpen]);

  // Memoize minDate to ensure it's stable and set to yesterday to allow selecting today
  const todayMinDate = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    return yesterday;
  }, []);

  // Helper function to format date as YYYY-MM-DD using local timezone (not UTC)
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOptionChange = (id: string, value: string) => {
    setOptions((prev) =>
      prev.map((opt) => (opt.id === id ? { ...opt, text: value } : opt))
    );
  };

  const addOption = () => {
    const newId = String(Date.now());
    setOptions((prev) => [...prev, { id: newId, text: "" }]);
  };

  const removeOption = (id: string) => {
    if (options.length > 2) {
      setOptions((prev) => prev.filter((opt) => opt.id !== id));
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validate form
    if (!formData.condo || !formData.title || !formData.description) {
      setError(t("polls.pleaseFillAllFields"));
      return;
    }

    if (!formData.startingDate || !formData.startingTime || !formData.endingDate || !formData.endingTime) {
      setError(t("polls.pleaseSelectAllDateTime"));
      return;
    }

    // Validate options
    const validOptions = options.filter((opt) => opt.text.trim());
    if (validOptions.length < 2) {
      setError(t("polls.pleaseAddAtLeastTwoOptions"));
      return;
    }

    try {
      setSubmitting(true);

      // Find condo ID from the selected condo name/number
      const selectedCondo = condominiums.find(
        c => c.condoNumber === formData.condo || c.name === formData.condo || c.id === formData.condo
      );

      if (!selectedCondo) {
        setError("Selected condominium not found");
        return;
      }

      // Combine date and time into ISO strings
      const startDateTime = new Date(`${formData.startingDate}T${formData.startingTime}`);
      const endDateTime = new Date(`${formData.endingDate}T${formData.endingTime}`);

      if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
        setError("Invalid date or time format");
        return;
      }

      if (startDateTime >= endDateTime) {
        setError("End date/time must be after start date/time");
        return;
      }

      // Create assembly first
      const assemblyResponse = await createAssembly({
        title: formData.title,
        description: formData.description,
        condoId: selectedCondo.id,
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
      });

      if (!assemblyResponse?.data || assemblyResponse?.status !== 'success') {
        throw new Error(assemblyResponse?.message || "Failed to create assembly");
      }

      const assemblyId = assemblyResponse.data.id;

      // Prepare agenda item data with proper formatting
      const agendaItemData = {
        title: formData.title.trim(),
        description: formData.description?.trim() || undefined,
        options: validOptions.map(opt => opt.text.trim()).filter(opt => opt.length > 0),
        votingStart: startDateTime.toISOString(),
        votingEnd: endDateTime.toISOString(),
      };

      // Ensure options array has at least 2 items
      if (agendaItemData.options.length < 2) {
        setError("Please add at least 2 poll options");
        return;
      }

      // Debug: Log the payload being sent
      console.log('Creating agenda item with data:', agendaItemData);

      // Create agenda item (the actual poll) within the assembly
      const agendaItemResponse = await createAgendaItem(assemblyId, agendaItemData);

      if (!agendaItemResponse?.data || agendaItemResponse?.status !== 'success') {
        throw new Error(agendaItemResponse?.message || t("polls.failedToLoadCondominiums"));
      }

      // Success - reset form and close modal
      toast.success(t("polls.pollCreated"));
      resetForm();
      toggle();
      
      // Notify parent component to refresh polls list
      if (onPollCreated) {
        onPollCreated();
      } else {
        // Fallback: reload the page
        window.location.reload();
      }
    } catch (err: any) {
      console.error("Error creating poll:", err);
      setError(err?.message || t("polls.failedToLoadCondominiums"));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      condo: "",
      title: "",
      description: "",
      startingDate: "",
      startingTime: "",
      endingDate: "",
      endingTime: "",
    });
    setOptions([
      { id: "1", text: "" },
      { id: "2", text: "" },
    ]);
  };

  const handleClose = () => {
    resetForm();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      size="lg"
      className="create-poll-modal"
    >
      <ModalHeader toggle={handleClose} className="create-poll-modal-header">
        {t("polls.createNewPoll")}
      </ModalHeader>
      <ModalBody className="create-poll-modal-body">
        <p className="create-poll-subtitle">
          {t("polls.defineQuestionDescription")}
        </p>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <Form onSubmit={handleSubmit} className="create-poll-form">
          <FormGroup className="create-poll-form-group">
            <Label for="condo" className="create-poll-label">
              {t("polls.condoNumber")} <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              id="condo"
              name="condo"
              value={formData.condo}
              onChange={handleInputChange}
              className="create-poll-input"
              required
              disabled={loading}
            >
              <option value="">{loading ? t("polls.loadingCondominiums") : t("polls.selectCondominium")}</option>
              {condominiums.map((condo) => (
                <option key={condo.id} value={condo.id}>
                  {condo.condoNumber || condo.name}
                </option>
              ))}
            </Input>
          </FormGroup>

          <Row className="create-poll-row">
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="startingDate" className="create-poll-label">
                  {t("polls.startingDate")} <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    key={`start-date-${isOpen}`}
                    className="create-poll-input"
                    placeholder={t("accessRequests.selectDate")}
                    value={formData.startingDate ? new Date(formData.startingDate + 'T00:00:00') : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const dateStr = formatDateLocal(date);
                        setFormData((prev) => ({
                          ...prev,
                          startingDate: dateStr,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          startingDate: "",
                        }));
                      }
                    }}
                    options={{
                      dateFormat: "Y-m-d",
                      minDate: todayMinDate,
                      allowInput: false,
                      clickOpens: true,
                      static: false,
                      wrap: false,
                    }}
                  />
                  <i className="ri-calendar-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="startingTime" className="create-poll-label">
                  {t("polls.startingTime")} <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    className="create-poll-input"
                    placeholder={t("accessRequests.selectTime")}
                    value={formData.startingTime ? (() => {
                      const timeParts = formData.startingTime.split(":");
                      const hours = timeParts[0] || "0";
                      const minutes = timeParts[1] || "0";
                      const date = new Date();
                      date.setHours(parseInt(hours, 10) || 0, parseInt(minutes, 10) || 0, 0, 0);
                      return date;
                    })() : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const hours = String(date.getHours()).padStart(2, "0");
                        const minutes = String(date.getMinutes()).padStart(2, "0");
                        setFormData((prev) => ({
                          ...prev,
                          startingTime: `${hours}:${minutes}`,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          startingTime: "",
                        }));
                      }
                    }}
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: "H:i",
                      time_24hr: true,
                      allowInput: false,
                      clickOpens: true,
                    }}
                  />
                  <i className="ri-time-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row className="create-poll-row">
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="endingDate" className="create-poll-label">
                  {t("polls.endingDate")} <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    className="create-poll-input"
                    placeholder={t("accessRequests.selectDate")}
                    value={formData.endingDate ? new Date(formData.endingDate + 'T00:00:00') : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const dateStr = formatDateLocal(date);
                        setFormData((prev) => ({
                          ...prev,
                          endingDate: dateStr,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          endingDate: "",
                        }));
                      }
                    }}
                    options={{
                      dateFormat: "Y-m-d",
                      minDate: formData.startingDate ? new Date(formData.startingDate + 'T00:00:00') : todayMinDate,
                      allowInput: false,
                      clickOpens: true,
                    }}
                  />
                  <i className="ri-calendar-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="create-poll-form-group">
                <Label for="endingTime" className="create-poll-label">
                  {t("polls.endingTime")} <span className="text-danger">*</span>
                </Label>
                <div className="create-poll-date-wrapper">
                  <Flatpickr
                    className="create-poll-input"
                    placeholder={t("accessRequests.selectTime")}
                    value={formData.endingTime ? (() => {
                      const timeParts = formData.endingTime.split(":");
                      const hours = timeParts[0] || "0";
                      const minutes = timeParts[1] || "0";
                      const date = new Date();
                      date.setHours(parseInt(hours, 10) || 0, parseInt(minutes, 10) || 0, 0, 0);
                      return date;
                    })() : undefined}
                    onChange={(dates) => {
                      const date = dates[0];
                      if (date) {
                        const hours = String(date.getHours()).padStart(2, "0");
                        const minutes = String(date.getMinutes()).padStart(2, "0");
                        setFormData((prev) => ({
                          ...prev,
                          endingTime: `${hours}:${minutes}`,
                        }));
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          endingTime: "",
                        }));
                      }
                    }}
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: "H:i",
                      time_24hr: true,
                      allowInput: false,
                      clickOpens: true,
                    }}
                  />
                  <i className="ri-time-line create-poll-icon"></i>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="create-poll-form-group">
            <Label for="title" className="create-poll-label">
              {t("polls.pollTitle")} <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder={t("polls.pollTitle")}
              className="create-poll-input"
              required
            />
          </FormGroup>

          <FormGroup className="create-poll-form-group">
            <Label for="description" className="create-poll-label">
              {t("polls.pollDescription")} <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t("polls.pollDescription")}
              rows={5}
              className="create-poll-textarea"
              required
            />
          </FormGroup>

          <FormGroup className="create-poll-form-group">
            <Label className="create-poll-label create-poll-options-label">
              {t("polls.pollOptions")} <span className="text-danger">*</span>
            </Label>
            <div className="create-poll-options-list">
              {options.map((option, idx) => (
                <div key={option.id} className="create-poll-option-item">
                  <Input
                    type="text"
                    placeholder={`${t("polls.option")} ${idx + 1}`}
                    value={option.text}
                    onChange={(e) => handleOptionChange(option.id, e.target.value)}
                    className="create-poll-input create-poll-option-input"
                    required={idx < 2}
                  />
                  {options.length > 2 && (
                    <Button
                      type="button"
                      color="danger"
                      size="sm"
                      outline
                      onClick={() => removeOption(option.id)}
                      className="create-poll-remove-btn"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              type="button"
              color="light"
              size="sm"
              onClick={addOption}
              className="create-poll-add-option-btn"
            >
              <i className="ri-add-line me-2"></i>
              {t("polls.addOption")}
            </Button>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter className="create-poll-modal-footer">
        <Button 
          color="secondary" 
          onClick={handleClose} 
          className="create-poll-cancel-btn"
          disabled={submitting}
        >
          {t("common.cancel")}
        </Button>
        <Button 
          color="primary" 
          onClick={handleSubmit} 
          className="create-poll-submit-btn"
          disabled={submitting || loading}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t("condominiums.creating")}
            </>
          ) : (
            <>
              {t("polls.createPoll")}
              <i className="ri-arrow-right-line ms-2"></i>
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreatePollModal;

